import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Check, ChevronRight, Gift, Heart, Mail as MailIcon } from "lucide-react";
import { PageHeader, PageFooter } from "@/components/SiteChrome";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Replace with Square's hosted e-gift card URL when ready, e.g.
// "https://squareup.com/gift/<MERCHANT_ID>/order"
const SQUARE_GIFT_URL = "#square-gift";

const PRESET_AMOUNTS = [
  { value: 55, label: "$55", note: "The Mini Reset" },
  { value: 111, label: "$111", note: "The Sacred Crown Ritual" },
  { value: 177, label: "$177", note: "The Soft Sanctuary" },
  { value: 222, label: "$222", note: "The Divine Feminine Ritual" },
];

const fieldClass =
  "w-full rounded-sm border border-[#eae0d5] bg-[#fdfbf7] px-4 py-3 text-[15px] font-light text-[#2d2926] placeholder:text-[#a8a098] focus:outline-none focus:border-[#b89461] transition-colors";

const labelClass =
  "block text-[11px] tracking-[0.28em] uppercase text-[#7a5e36] mb-2 font-medium";

export default function GiftCards() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [presetAmount, setPresetAmount] = useState(111);
  const [customAmount, setCustomAmount] = useState("");
  const [form, setForm] = useState({
    recipient_name: "",
    recipient_email: "",
    sender_name: "",
    sender_email: "",
    personal_message: "",
    delivery_date: "",
  });

  const amount = useMemo(() => {
    if (customAmount) {
      const n = parseInt(customAmount, 10);
      return Number.isFinite(n) ? n : 0;
    }
    return presetAmount;
  }, [presetAmount, customAmount]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (amount < 25 || amount > 1000) {
      toast.error("Please choose an amount between $25 and $1000.");
      return;
    }
    if (!form.recipient_name || !form.recipient_email || !form.sender_name || !form.sender_email) {
      toast.error("Please fill in both names and emails.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/gift-cards`, {
        amount,
        ...form,
        delivery_date: form.delivery_date || null,
      });
      setOrder(data);
      toast.success("Gift card details saved. Continue to payment.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (order) {
    return (
      <div className="min-h-screen bg-[#fdfbf7] text-[#2d2926] font-body">
        <Toaster richColors position="top-center" />
        <PageHeader />
        <main data-testid="gift-success" className="mx-auto max-w-2xl px-6 sm:px-10 py-20 md:py-28 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-[#b89461]/15 flex items-center justify-center">
              <Gift className="text-[#b89461]" strokeWidth={1.4} />
            </div>
            <p className="mt-8 font-display text-[11px] tracking-[0.45em] text-[#b89461]">
              ALMOST READY TO DELIVER
            </p>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl uppercase tracking-tight leading-[1.15] text-[#2d2926]">
              A <span className="italic text-[#7a5e36] normal-case">${order.amount}</span> Gift Waits
              <span className="block">for {order.recipient_name.split(" ")[0]}.</span>
            </h1>
            <p className="mt-6 text-[15px] leading-[1.85] text-[#3f3a35] font-light">
              Your details are saved. Complete the secure payment below and your e-gift
              will be emailed to {order.recipient_name} on the date you chose
              {order.delivery_date ? "." : " — today."}
            </p>

            <a
              href={SQUARE_GIFT_URL}
              data-testid="gift-pay-square"
              className="mt-10 inline-flex items-center justify-center gap-3 rounded-sm bg-[#b89461] px-9 py-4 text-[11px] font-medium tracking-[0.28em] uppercase text-[#fdfbf7] transition-all duration-500 hover:-translate-y-0.5 hover:bg-[#a07e4f]"
            >
              Pay ${order.amount} Securely via Square <ChevronRight size={16} />
            </a>
            <p className="mt-4 text-[11px] tracking-[0.18em] uppercase text-[#a8a098]">
              Payment processed by Square · No card data touches this site
            </p>

            <button
              onClick={() => {
                setOrder(null);
                navigate("/");
              }}
              className="mt-10 text-[12px] tracking-[0.2em] uppercase text-[#7a5e36] hover:text-[#b89461]"
            >
              ← Back to home
            </button>
          </motion.div>
        </main>
        <PageFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfbf7] text-[#2d2926] font-body">
      <Toaster richColors position="top-center" />
      <PageHeader />

      <main className="mx-auto max-w-3xl px-6 sm:px-10 py-16 md:py-20" data-testid="gift-cards-page">
        <div className="text-center">
          <p className="font-display text-[11px] tracking-[0.45em] text-[#b89461]">E-GIFT CARDS</p>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-[1.1] text-[#2d2926]">
            Give The Gift Of
            <span className="block text-[#7a5e36]">Being Cared For.</span>
          </h1>
          <p className="mt-6 max-w-xl mx-auto text-[15px] leading-[1.85] text-[#3f3a35] font-light">
            Delivered instantly by email, redeemable for any session. Perfect for
            mothers, daughters, sisters, best friends, and women who deserve a moment
            of rest.
          </p>
        </div>

        {/* Gift card preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mt-14 mx-auto max-w-md relative"
        >
          <div className="aspect-[8/5] rounded-[1.25rem] bg-gradient-to-br from-[#f6f1ea] via-[#fdfbf7] to-[#e8d1d1]/40 border border-[#eae0d5] shadow-[0_30px_60px_-30px_rgba(122,94,54,0.25)] p-7 flex flex-col justify-between">
            <div className="flex items-start justify-between">
              <div className="leading-tight">
                <p className="font-display text-[10px] tracking-[0.45em] text-[#b89461]">SACRED CROWN</p>
                <p className="font-display text-[8px] tracking-[0.5em] text-[#b89461]">STUDIO</p>
              </div>
              <Gift className="text-[#b89461]" size={20} strokeWidth={1.2} />
            </div>
            <div>
              <p className="font-display italic text-[#7a5e36] text-sm">A gift of care & calm</p>
              <p className="font-display text-5xl text-[#2d2926] mt-1">
                ${amount || 0}
              </p>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#5c544f] mt-2">
                {form.recipient_name ? `For ${form.recipient_name}` : "FOR HER"}
              </p>
            </div>
          </div>
        </motion.div>

        <form onSubmit={onSubmit} className="mt-14 space-y-10" data-testid="gift-form">
          {/* Amount */}
          <section className="space-y-5">
            <h2 className="font-display text-xl uppercase tracking-[0.12em] text-[#2d2926]">
              Choose an Amount
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {PRESET_AMOUNTS.map((a) => {
                const active = !customAmount && presetAmount === a.value;
                return (
                  <button
                    type="button"
                    key={a.value}
                    data-testid={`gift-amount-${a.value}`}
                    onClick={() => {
                      setPresetAmount(a.value);
                      setCustomAmount("");
                    }}
                    className={`rounded-sm border px-3 py-4 text-center transition-all ${
                      active
                        ? "border-[#b89461] bg-[#b89461]/10"
                        : "border-[#eae0d5] hover:border-[#d0b49f]"
                    }`}
                  >
                    <p className="font-display text-2xl text-[#2d2926]">{a.label}</p>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-[#7a5e36] mt-1">
                      {a.note}
                    </p>
                  </button>
                );
              })}
            </div>
            <div>
              <label className={labelClass}>Or enter a custom amount (between $25–$1000)</label>
              <input
                type="number"
                min={25}
                max={1000}
                step={1}
                data-testid="gift-amount-custom"
                className={fieldClass}
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                placeholder="e.g. 88"
              />
            </div>
          </section>

          {/* Recipient */}
          <section className="space-y-5">
            <h2 className="font-display text-xl uppercase tracking-[0.12em] text-[#2d2926]">
              For Her
            </h2>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Recipient Name *</label>
                <input
                  data-testid="gift-recipient-name"
                  className={fieldClass}
                  value={form.recipient_name}
                  onChange={set("recipient_name")}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Recipient Email *</label>
                <input
                  type="email"
                  data-testid="gift-recipient-email"
                  className={fieldClass}
                  value={form.recipient_email}
                  onChange={set("recipient_email")}
                  required
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Personal Message</label>
              <textarea
                rows={4}
                maxLength={400}
                data-testid="gift-message"
                className={fieldClass + " resize-none"}
                value={form.personal_message}
                onChange={set("personal_message")}
                placeholder="A few soft words she can carry into her session…"
              />
              <p className="mt-2 text-[11px] text-[#a8a098]">
                {form.personal_message.length}/400
              </p>
            </div>
            <div>
              <label className={labelClass}>Deliver On</label>
              <input
                type="date"
                data-testid="gift-delivery-date"
                className={fieldClass}
                value={form.delivery_date}
                onChange={set("delivery_date")}
              />
              <p className="mt-2 text-[12px] text-[#7a5e36] font-light">
                Leave blank to send right after payment.
              </p>
            </div>
          </section>

          {/* Sender */}
          <section className="space-y-5">
            <h2 className="font-display text-xl uppercase tracking-[0.12em] text-[#2d2926]">
              From You
            </h2>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Your Name *</label>
                <input
                  data-testid="gift-sender-name"
                  className={fieldClass}
                  value={form.sender_name}
                  onChange={set("sender_name")}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Your Email *</label>
                <input
                  type="email"
                  data-testid="gift-sender-email"
                  className={fieldClass}
                  value={form.sender_email}
                  onChange={set("sender_email")}
                  required
                />
              </div>
            </div>
          </section>

          {/* Summary + CTA */}
          <section className="rounded-sm border border-[#eae0d5] bg-[#f6f1ea]/60 p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-[#3f3a35]">
                <Heart className="text-[#b89461]" size={18} strokeWidth={1.2} />
                <p className="text-[13px] font-light">
                  Total today
                </p>
              </div>
              <p className="font-display text-2xl text-[#2d2926]" data-testid="gift-total">
                ${amount || 0}
              </p>
            </div>
            <p className="mt-3 text-[12px] text-[#7a5e36] font-light flex items-start gap-2">
              <MailIcon size={14} className="mt-[2px]" strokeWidth={1.4} />
              <span>
                After payment, the recipient receives a beautifully designed e-gift
                with your personal message + a redemption code.
              </span>
            </p>
          </section>

          <div className="flex flex-col items-center gap-4">
            <button
              type="submit"
              disabled={submitting}
              data-testid="gift-submit"
              className="inline-flex items-center justify-center gap-3 rounded-sm bg-[#b89461] px-12 py-4 text-[11px] font-medium tracking-[0.28em] uppercase text-[#fdfbf7] transition-all duration-500 hover:-translate-y-0.5 hover:bg-[#a07e4f] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Saving…" : "Continue To Payment"}
              {!submitting && <ChevronRight size={16} />}
            </button>
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#a8a098]">
              Secure payment via Square · No card data touches this site
            </p>
            <Link to="/" className="text-[12px] tracking-[0.2em] uppercase text-[#7a5e36] hover:text-[#b89461]">
              ← Back to home
            </Link>
          </div>
        </form>
      </main>

      <PageFooter />
    </div>
  );
}
